import { numFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Skeleton,
  Menu,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Popover,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import BuildIcon from '@mui/icons-material/Build';
import DescriptionIcon from '@mui/icons-material/Description';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CloseIcon from '@mui/icons-material/Close';
import { withTranslation } from 'react-i18next';
import { getDateFnsLocale } from '../../utils/languages';
import { useNavigate } from 'react-router-dom';
import {
  format,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  isWithinInterval,
  parseISO,
  isSameWeek,
  isSameDay,
  differenceInCalendarDays,
} from 'date-fns';
import axios from 'axios';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { useUserStore } from '../../store';
import { extractListFromResponse } from '../../utils/apiUtils';
import DataTable from '../shared/DataTable';
import WorkDayDialog from './WorkDayDialog';
import WorkTimeRangePicker from './WorkTimeRangePicker';
import ManagerBoardView from './clock/ManagerBoardView';

import { can } from '../../utils/access';
import { formatQuantity } from '../../utils';
const WorkTimePage = ({ t, i18n }) => {
  const { currentOrganization, user } = useUserStore();
  const navigate = useNavigate();
  const isManagerRole = can(currentOrganization, 'timeclock.board');
  const locale = getDateFnsLocale(i18n.language);

  // The live "who's working now" board is a tab here (not a top-level menu item) and only for
  // managers when the timer-based time clock is enabled.
  const timeClockEnabled = currentOrganization?.time_clock_enabled ?? false;
  const showBoardTab = isManagerRole && timeClockEnabled;
  const [tab, setTab] = useState(0);

  // State
  const [workDays, setWorkDays] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Find current user's employee record in this organization (for auto-selecting in dialog)
  const currentUserEmployeeId = useMemo(() => {
    if (!user?.id || !employees.length) return null;
    const myEmployee = employees.find((emp) => emp.user === user.id);
    return myEmployee?.id || null;
  }, [user?.id, employees]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The visible period. Defaults to the current week (the week arrows stay the primary UX), but a
  // free range can be picked — a pay period (1.-15. / 16.-end of month, or a month) is not a week.
  const [rangeStart, setRangeStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [rangeEnd, setRangeEnd] = useState(() => endOfWeek(new Date(), { weekStartsOn: 1 }));
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [rangeAnchor, setRangeAnchor] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWorkDay, setEditingWorkDay] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogEmployee, setDialogEmployee] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuWorkDay, setMenuWorkDay] = useState(null);
  const [expandedWorkDays, setExpandedWorkDays] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [workDayToDelete, setWorkDayToDelete] = useState(null);
  const [workDayDetails, setWorkDayDetails] = useState({});
  const [unloggedDays, setUnloggedDays] = useState([]);
  // Unconfirmed (tracked-but-not-confirmed) days. Own/selected-employee → flat dates; manager
  // "All employees" → per-employee aggregate. Only meaningful when the live time clock is on.
  const [pendingDays, setPendingDays] = useState([]);
  const [pendingByEmployee, setPendingByEmployee] = useState([]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const startDate = format(rangeStart, 'yyyy-MM-dd');
      const endDate = format(rangeEnd, 'yyyy-MM-dd');

      const params = {
        date_from: startDate,
        date_to: endDate,
      };
      if (selectedEmployee) {
        params.employee = selectedEmployee;
      }

      const requests = [
        axios.get('/api/scheduling/work-days/', { params }),
      ];

      // Fetch employees only for managers
      if (isManagerRole) {
        requests.push(axios.get('/api/employees/'));
      }

      const responses = await Promise.all(requests);
      // Handle paginated responses
      const { data: workDaysData } = extractListFromResponse(responses[0]);
      setWorkDays(workDaysData);
      if (isManagerRole && responses[1]) {
        const { data: employeesData } = extractListFromResponse(responses[1]);
        setEmployees(employeesData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd, selectedEmployee, isManagerRole]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch unlogged days (only for current user, not managers viewing others)
  const fetchUnloggedDays = useCallback(async () => {
    if (isManagerRole && selectedEmployee) {
      // Don't show unlogged days reminder when manager is viewing someone else's data
      setUnloggedDays([]);
      return;
    }
    try {
      const response = await axios.get('/api/scheduling/work-days/unlogged_days/');
      setUnloggedDays(response.data);
    } catch (err) {
      console.error('Error fetching unlogged days:', err);
    }
  }, [isManagerRole, selectedEmployee]);

  useEffect(() => {
    fetchUnloggedDays();
  }, [fetchUnloggedDays]);

  // Unconfirmed work days (live time clock only): own as employee, the selected employee for a
  // manager, or everyone (?scope=all) when a manager has no employee selected. Surfacing these
  // on the page the boss actually reviews is what stops a tracked day from quietly going missing.
  const fetchPendingConfirmations = useCallback(async () => {
    if (!timeClockEnabled) {
      setPendingDays([]);
      setPendingByEmployee([]);
      return;
    }
    try {
      if (isManagerRole && !selectedEmployee) {
        const res = await axios.get('/api/scheduling/timeclock/pending/', { params: { scope: 'all' } });
        setPendingByEmployee(res.data?.by_employee || []);
        setPendingDays([]);
      } else {
        const params = {};
        if (isManagerRole && selectedEmployee) params.employee_id = selectedEmployee;
        const res = await axios.get('/api/scheduling/timeclock/pending/', { params });
        setPendingDays(res.data?.dates || []);
        setPendingByEmployee([]);
      }
    } catch (err) {
      setPendingDays([]);
      setPendingByEmployee([]);
    }
  }, [timeClockEnabled, isManagerRole, selectedEmployee]);

  useEffect(() => {
    fetchPendingConfirmations();
  }, [fetchPendingConfirmations]);

  // Open the reconcile screen for a given date (+ employee for a manager acting on someone else).
  const goToReconcile = (dateStr, employeeId) => {
    const empParam = employeeId ? `&employee=${employeeId}` : '';
    navigate(`/time-clock/confirm?date=${dateStr}${empParam}`);
  };

  // Week navigation. Show one whole week and leave custom-range mode — the arrows always mean
  // "step a week", also when a free range is on screen (they step from the week the range starts in).
  const showWeek = useCallback((dateInWeek) => {
    const weekStart = startOfWeek(dateInWeek, { weekStartsOn: 1 });
    setRangeStart(weekStart);
    setRangeEnd(endOfWeek(weekStart, { weekStartsOn: 1 }));
    setIsCustomRange(false);
  }, []);

  const goToPreviousWeek = () => showWeek(subWeeks(rangeStart, 1));
  const goToNextWeek = () => showWeek(addWeeks(rangeStart, 1));
  const goToThisWeek = () => showWeek(new Date());

  // Free date range picked from the calendar popover. A range that happens to be exactly one
  // Mon-Sun week goes back to plain week mode, so the arrows and the "this week" button behave
  // as usual and the header doesn't claim a custom range.
  const applyCustomRange = (start, end) => {
    setRangeAnchor(null);
    if (isSameDay(start, startOfWeek(start, { weekStartsOn: 1 })) && isSameDay(end, endOfWeek(start, { weekStartsOn: 1 }))) {
      showWeek(start);
      return;
    }
    setRangeStart(start);
    setRangeEnd(end);
    setIsCustomRange(true);
  };

  const isCurrentWeek = !isCustomRange && isSameWeek(rangeStart, new Date(), { weekStartsOn: 1 });
  const rangeDayCount = differenceInCalendarDays(rangeEnd, rangeStart) + 1;

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    let totalHours = 0;
    let totalBlocks = 0;

    workDays.forEach((wd) => {
      totalHours += (wd.work_hours != null ? wd.work_hours : wd.total_hours) || 0;
      totalBlocks += wd.block_count || 0;
    });

    return {
      totalHours: formatQuantity(totalHours),
      daysLogged: workDays.length,
      blockCount: totalBlocks,
    };
  }, [workDays]);

  // Sort work days by date (descending)
  const sortedWorkDays = useMemo(() => {
    return [...workDays].sort((a, b) => b.work_date.localeCompare(a.work_date));
  }, [workDays]);

  // Per-employee totals for the visible period. Payroll runs per employee, so a manager looking at
  // "All employees" over a pay period shouldn't have to switch the filter person by person.
  // Derived from the already-fetched list — no extra request.
  const employeeTotals = useMemo(() => {
    const byEmployee = new Map();
    workDays.forEach((wd) => {
      const key = wd.employee;
      const entry = byEmployee.get(key) || { id: key, name: wd.employee_name || '', hours: 0, days: 0 };
      entry.hours += (wd.work_hours != null ? wd.work_hours : wd.total_hours) || 0;
      entry.days += 1;
      byEmployee.set(key, entry);
    });
    return [...byEmployee.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [workDays]);

  const showEmployeeTotals = isManagerRole && !selectedEmployee && employeeTotals.length > 1;

  const employeeTotalColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('Employee'),
        maxWidth: 320,
        render: (row) => row.name,
      },
      {
        key: 'hours',
        label: t('worktime.range.byEmployee.hours'),
        align: 'right',
        width: '1%',
        noWrap: true,
        render: (row) => `${formatQuantity(row.hours)} h`,
      },
      {
        key: 'days',
        label: t('worktime.range.byEmployee.days'),
        align: 'right',
        width: '1%',
        noWrap: true,
        render: (row) => row.days,
      },
    ],
    [t],
  );

  // Menu handlers
  const handleMenuOpen = (event, workDay) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuWorkDay(workDay);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuWorkDay(null);
  };

  const handleEdit = async () => {
    if (menuWorkDay) {
      // Fetch full work day data with blocks
      try {
        const response = await axios.get(`/api/scheduling/work-days/${menuWorkDay.id}/`);
        setEditingWorkDay(response.data);
        setSelectedDate(response.data.work_date);
        setDialogOpen(true);
      } catch (err) {
        console.error('Error fetching work day:', err);
      }
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (menuWorkDay) {
      setWorkDayToDelete(menuWorkDay);
      setDeleteConfirmOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (workDayToDelete) {
      try {
        await axios.delete(`/api/scheduling/work-days/${workDayToDelete.id}/`);
        setWorkDays(workDays.filter((wd) => wd.id !== workDayToDelete.id));
      } catch (err) {
        console.error('Error deleting work day:', err);
      }
    }
    setDeleteConfirmOpen(false);
    setWorkDayToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setWorkDayToDelete(null);
  };

  const handleAddWorkDay = () => {
    setEditingWorkDay(null);
    setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
    // Auto-select current user's employee for managers (if they have an employee record)
    setDialogEmployee(isManagerRole ? currentUserEmployeeId : null);
    setDialogOpen(true);
  };

  const handleFillUnloggedDay = (dateStr) => {
    setEditingWorkDay(null);
    setSelectedDate(dateStr);
    // Auto-select current user's employee for managers (if they have an employee record)
    setDialogEmployee(isManagerRole ? currentUserEmployeeId : null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingWorkDay(null);
    setSelectedDate(null);
    setDialogEmployee(null);
  };

  const handleWorkDaySaved = (savedWorkDay) => {
    // The full work-day serializer (PUT/POST response) omits the list-only block_count — derive
    // it from the returned blocks so the summary row stays correct after an edit.
    const normalized = {
      ...savedWorkDay,
      block_count: savedWorkDay.block_count != null ? savedWorkDay.block_count : savedWorkDay.blocks?.length || 0,
    };
    const workDayDate = parseISO(normalized.work_date);
    const isInVisibleRange = isWithinInterval(workDayDate, {
      start: rangeStart,
      end: rangeEnd,
    });

    if (isInVisibleRange) {
      // Check if we're updating an existing work day
      const existingIndex = workDays.findIndex((wd) => wd.id === normalized.id);
      if (existingIndex >= 0) {
        // Update existing
        setWorkDays(workDays.map((wd) => (wd.id === normalized.id ? normalized : wd)));
      } else {
        // Add new
        setWorkDays([...workDays, normalized]);
      }
    } else {
      // Navigate to the week containing the saved work day
      showWeek(workDayDate);
    }

    // Refresh the expanded-details cache so an open accordion shows the saved blocks immediately
    // (it reads from workDayDetails, which otherwise keeps the stale version until a manual refresh).
    if (savedWorkDay.blocks) {
      setWorkDayDetails((prev) => ({ ...prev, [savedWorkDay.id]: savedWorkDay }));
    }

    // Refresh unlogged days list since a day was filled
    fetchUnloggedDays();

    handleDialogClose();
  };

  const formatDateHeader = (dateStr) => {
    const date = parseISO(dateStr);
    return format(date, 'EEEE d.M.', { locale });
  };

  // Handle accordion expansion
  const handleAccordionChange = async (workDayId) => {
    const isExpanded = expandedWorkDays[workDayId];

    setExpandedWorkDays((prev) => ({
      ...prev,
      [workDayId]: !isExpanded,
    }));

    // Fetch details if expanding and not already fetched
    if (!isExpanded && !workDayDetails[workDayId]) {
      try {
        const response = await axios.get(`/api/scheduling/work-days/${workDayId}/`);
        setWorkDayDetails((prev) => ({
          ...prev,
          [workDayId]: response.data,
        }));
      } catch (err) {
        console.error('Error fetching work day details:', err);
      }
    }
  };

  const formatTime = (timeStr) => {
    // Format HH:MM:SS to HH:MM
    return timeStr ? timeStr.substring(0, 5) : '';
  };

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">
          {t('Error')}: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="worktime-page">
      {showBoardTab && (
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={t('worktime.tabs.entries')} data-testid="worktime-tab-entries" />
          <Tab label={t('worktime.tabs.liveBoard')} data-testid="worktime-tab-board" />
        </Tabs>
      )}

      {showBoardTab && tab === 1 ? (
        <ManagerBoardView />
      ) : (
        <>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            component="h4"
            variant="h4"
            data-testid="worktime-heading"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('Work Time')}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'text.secondary',
              mt: 0.5,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {t('Track your daily work hours')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleAddWorkDay}
          startIcon={<AddIcon />}
          data-testid="add-workday-button"
          sx={{
            bgcolor: 'secondary.main',
            color: 'white',
            px: 2.5,
            py: 1.5,
            borderRadius: '10px',
            fontWeight: 500,
            fontSize: '14px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
            '&:hover': {
              bgcolor: 'secondary.light',
              boxShadow: '0 6px 16px rgba(49, 130, 206, 0.4)',
            },
          }}
        >
          {t('Add Work Day')}
        </Button>
      </Box>

      {/* Unconfirmed work days — tracked time that was never confirmed (so it isn't saved as
          work-time yet). Surfaced here for the worker and the manager; one tap to confirm. */}
      {(pendingDays.length > 0 || pendingByEmployee.length > 0) && (
        <Alert severity="warning" icon={<EventBusyIcon />} sx={{ mb: 3 }} data-testid="unconfirmed-days-alert">
          <AlertTitle sx={{ fontWeight: 600 }}>{t('worktime.pending.title')}</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('worktime.pending.body')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {pendingDays.map((d) => (
              <Chip
                key={d}
                label={format(parseISO(d), 'EEEEEE d.M.', { locale })}
                onClick={() => goToReconcile(d, isManagerRole ? selectedEmployee : null)}
                size="small"
                sx={{
                  cursor: 'pointer',
                  fontWeight: 500,
                  bgcolor: 'rgba(237, 137, 54, 0.15)',
                  '&:hover': { bgcolor: 'rgba(237, 137, 54, 0.25)' },
                }}
              />
            ))}
            {pendingByEmployee.map((emp) =>
              emp.dates.map((d) => (
                <Chip
                  key={`${emp.employee}-${d}`}
                  label={`${emp.employee_name} · ${format(parseISO(d), 'd.M.', { locale })}`}
                  onClick={() => goToReconcile(d, emp.employee)}
                  size="small"
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 500,
                    bgcolor: 'rgba(237, 137, 54, 0.15)',
                    '&:hover': { bgcolor: 'rgba(237, 137, 54, 0.25)' },
                  }}
                />
              )),
            )}
          </Box>
        </Alert>
      )}

      {/* Unlogged Days Alert */}
      {unloggedDays.length > 0 && (
        <Alert
          severity="warning"
          icon={<AccessTimeFilledIcon />}
          sx={{ mb: 3 }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>
            {t('Unlogged work hours')}
          </AlertTitle>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('You have {{count}} days with unlogged work hours. Click a date to fill:', { count: unloggedDays.length })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {unloggedDays.slice(0, 7).map((day) => (
              <Chip
                key={day.date}
                label={format(parseISO(day.date), 'EEEEEE d.M.', { locale })}
                onClick={() => handleFillUnloggedDay(day.date)}
                size="small"
                sx={{
                  cursor: 'pointer',
                  fontWeight: 500,
                  bgcolor: 'rgba(237, 137, 54, 0.15)',
                  '&:hover': {
                    bgcolor: 'rgba(237, 137, 54, 0.25)',
                  },
                }}
              />
            ))}
            {unloggedDays.length > 7 && (
              <Chip
                label={`+${unloggedDays.length - 7} ${t('more')}`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>
        </Alert>
      )}

      {/* Period Navigation & Employee Filter */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        {/* Period Navigation: week arrows (unchanged) + free date range */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderRadius: '10px',
          }}
        >
          <IconButton onClick={goToPreviousWeek} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <Box sx={{ minWidth: 180, textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              {format(rangeStart, 'd.M.', { locale })} -{' '}
              {format(rangeEnd, 'd.M.yyyy', { locale })}
            </Typography>
            {isCustomRange && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}
                data-testid="worktime-custom-range-label"
              >
                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                  {t('worktime.range.customLabel', { days: rangeDayCount })}
                </Typography>
                <IconButton
                  onClick={goToThisWeek}
                  size="small"
                  title={t('worktime.range.backToWeek')}
                  data-testid="worktime-range-clear"
                  sx={{ p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )}
          </Box>
          <IconButton onClick={goToNextWeek} size="small">
            <ChevronRightIcon />
          </IconButton>
          <IconButton
            onClick={goToThisWeek}
            size="small"
            disabled={isCurrentWeek}
            sx={{ ml: 1 }}
            title={t('This week')}
          >
            <TodayIcon />
          </IconButton>
          <IconButton
            onClick={(e) => setRangeAnchor(e.currentTarget)}
            size="small"
            title={t('worktime.range.choose')}
            data-testid="worktime-range-button"
            color={isCustomRange ? 'secondary' : 'default'}
          >
            <DateRangeIcon />
          </IconButton>
        </Paper>

        <Popover
          open={Boolean(rangeAnchor)}
          anchorEl={rangeAnchor}
          onClose={() => setRangeAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <WorkTimeRangePicker
            start={rangeStart}
            end={rangeEnd}
            locale={locale}
            onApply={applyCustomRange}
            onCancel={() => setRangeAnchor(null)}
          />
        </Popover>

        {/* Employee Filter (for managers only) */}
        {isManagerRole && employees.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('Employee')}</InputLabel>
            <Select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              label={t('Employee')}
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="">{t('All employees')}</MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Summary Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: 'rgba(49, 130, 206, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'secondary.main',
              }}
            >
              <AccessTimeFilledIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                {t('Total Hours')}
              </Typography>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'text.primary' }}>
                {loading ? <Skeleton width={60} /> : `${summaryStats.totalHours}\u00a0h`}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: 'rgba(56, 161, 105, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'success.main',
              }}
            >
              <CalendarTodayIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                {t('Days Logged')}
              </Typography>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'text.primary' }}>
                {loading ? <Skeleton width={40} /> : summaryStats.daysLogged}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: 'rgba(214, 158, 46, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'warning.main',
              }}
            >
              <DescriptionIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                {t('Time Blocks')}
              </Typography>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'text.primary' }}>
                {loading ? <Skeleton width={40} /> : summaryStats.blockCount}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Per-employee totals for the visible period (managers viewing everyone) */}
      {!loading && showEmployeeTotals && (
        <Box sx={{ mb: 3 }} data-testid="worktime-employee-totals">
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.secondary', mb: 1.5, px: 1 }}>
            {t('worktime.range.byEmployee.title')}
          </Typography>
          <DataTable
            columns={employeeTotalColumns}
            data={employeeTotals}
            onRowClick={(row) => setSelectedEmployee(row.id)}
          />
        </Box>
      )}

      {/* Work Days List */}
      {loading ? (
        <Box>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={100} sx={{ mb: 2, borderRadius: 2 }} />
          ))}
        </Box>
      ) : sortedWorkDays.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: '12px' }}>
          <AccessTimeFilledIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            {isCustomRange ? t('worktime.range.emptyRange') : t('No work days logged for this week')}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {t('Click the button above to add your first work day')}
          </Typography>
        </Paper>
      ) : (
        sortedWorkDays.map((workDay) => (
          <Box key={workDay.id} sx={{ mb: 3 }}>
            {/* Date Header */}
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'capitalize',
                mb: 1.5,
                px: 1,
              }}
            >
              {formatDateHeader(workDay.work_date)}
            </Typography>

            {/* Work Day Accordion */}
            <Accordion
              data-testid={`workday-row-${workDay.id}`}
              expanded={expandedWorkDays[workDay.id] || false}
              onChange={() => handleAccordionChange(workDay.id)}
              elevation={0}
              sx={{
                borderRadius: '12px !important',
                overflow: 'hidden',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 2,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    my: 1.5,
                  },
                }}
              >
                {/* Time Summary */}
                <Box sx={{ width: 100, flexShrink: 0 }}>
                  <Typography
                    data-testid="workday-hours"
                    sx={{
                      fontSize: '20px',
                      fontWeight: 700,
                      color: 'text.primary',
                      ...numFont,
                    }}
                  >
                    {formatQuantity(workDay.work_hours != null ? workDay.work_hours : workDay.total_hours)}{'\u00a0'}h
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    {workDay.block_count} {workDay.block_count === 1 ? t('block') : t('blocks')}
                  </Typography>
                </Box>

                {/* Employee name (for managers) */}
                {isManagerRole && !selectedEmployee && (
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: 'text.secondary',
                      ml: 2,
                      display: { xs: 'none', sm: 'block' },
                    }}
                  >
                    {workDay.employee_name}
                  </Typography>
                )}

                {/* Confirmation badge (live time clock only) — never let an unconfirmed day look done */}
                {timeClockEnabled && (
                  <Chip
                    size="small"
                    label={
                      workDay.confirmed_at
                        ? t('worktime.confirmedBadge')
                        : t('worktime.notConfirmedBadge')
                    }
                    sx={{
                      ml: 2,
                      height: 20,
                      fontSize: '10px',
                      fontWeight: 600,
                      bgcolor: workDay.confirmed_at ? 'rgba(56, 161, 105, 0.12)' : 'rgba(214, 158, 46, 0.15)',
                      color: workDay.confirmed_at ? 'success.main' : 'warning.main',
                    }}
                  />
                )}

                {/* Actions */}
                <Box sx={{ ml: 'auto', mr: 1 }}>
                  <IconButton
                    data-testid="workday-menu-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuOpen(e, workDay);
                    }}
                    sx={{
                      color: 'text.disabled',
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                <Divider sx={{ mb: 2 }} />
                {workDayDetails[workDay.id]?.blocks ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {workDayDetails[workDay.id].blocks.map((block, index) => (
                      <Box
                        key={block.id || index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1.5,
                          bgcolor: 'grey.50',
                          borderRadius: '8px',
                        }}
                      >
                        {/* Time */}
                        <Box sx={{ width: { xs: 100, sm: 130 }, flexShrink: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontWeight: 600,
                              color: 'text.primary',
                              ...numFont,
                              whiteSpace: { sm: 'nowrap' },
                            }}
                          >
                            {formatTime(block.start_time)} - {formatTime(block.end_time)}
                          </Typography>
                          <Typography data-testid="block-duration" sx={{ fontSize: '12px', color: 'text.secondary', ...numFont }}>
                            {formatQuantity(block.duration_hours)}{'\u00a0'}h
                          </Typography>
                        </Box>

                        {/* Activity */}
                        <Box sx={{ flex: 1, ml: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {block.activity_type === 'maintenance' ? (
                              <BuildIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                            ) : block.activity_type === 'break' ? (
                              <RestaurantIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                            ) : (
                              <DescriptionIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                            )}
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                              {block.activity_type === 'maintenance'
                                ? [
                                    block.maintenance_info?.work_number && `#${block.maintenance_info.work_number}`,
                                    block.maintenance_info?.vehicle_registration,
                                  ].filter(Boolean).join(' • ') || t('Maintenance')
                                : block.activity_type === 'break'
                                ? t('worktime.reconcile.break')
                                : block.other_description || t('worktime.reconcile.otherWork')}
                            </Typography>
                            <Chip
                              label={
                                block.activity_type === 'maintenance'
                                  ? t('Maintenance')
                                  : block.activity_type === 'break'
                                  ? t('worktime.reconcile.break')
                                  : t('worktime.reconcile.otherWork')
                              }
                              size="small"
                              sx={{
                                fontSize: '10px',
                                height: '20px',
                                bgcolor:
                                  block.activity_type === 'maintenance'
                                    ? 'rgba(49, 130, 206, 0.1)'
                                    : 'rgba(214, 158, 46, 0.1)',
                                color:
                                  block.activity_type === 'maintenance'
                                    ? 'secondary.main'
                                    : 'warning.main',
                              }}
                            />
                          </Box>
                          {block.maintenance_info && (
                            <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.25 }}>
                              {block.maintenance_info.customer_name}
                            </Typography>
                          )}
                          {block.notes && (
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: 'text.secondary',
                                fontStyle: 'italic',
                                mt: 0.5,
                              }}
                            >
                              {block.notes}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                      {t('Loading...')}
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        ))
      )}

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {t('Edit')}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t('Delete')}
        </MenuItem>
      </Menu>

      {/* Work Day Dialog */}
      <WorkDayDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        workDay={editingWorkDay}
        onSaved={handleWorkDaySaved}
        employees={employees}
        isManagerRole={isManagerRole}
        selectedEmployee={dialogEmployee}
        selectedDate={selectedDate}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">{t('Delete Work Day')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('Are you sure you want to delete this work day? This action cannot be undone.')}
          </DialogContentText>
          {workDayToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatDateHeader(workDayToDelete.work_date)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatQuantity(workDayToDelete.total_hours)}{'\u00a0'}h - {workDayToDelete.block_count}{' '}
                {workDayToDelete.block_count === 1 ? t('block') : t('blocks')}
              </Typography>
              {isManagerRole && workDayToDelete.employee_name && (
                <Typography variant="body2" color="text.secondary">
                  {workDayToDelete.employee_name}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('Cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
};

export default withTranslation()(WorkTimePage);
